import { useEffect, useState } from "react";

/**
 * Returns `true` once `window.scrollY` exceeds `threshold` (px). Uses a
 * passive listener so it never blocks scroll perf, and a single state flip
 * (no re-renders for every pixel).
 *
 * Used by the public-site `<Header>` to switch from transparent-over-hero
 * to solid-on-scroll. Threshold of 40 px is the default agreed in
 * founder-decisions §7.3 — small enough that the transition kicks in just
 * as the user starts scrolling the hero away.
 */
export function useScrolledPast(threshold: number): boolean {
  // SSR-safe initial: there is no `window` server-side, default to false.
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const evaluate = () => setScrolled(window.scrollY > threshold);

    // Snapshot on mount in case the user lands on a deep-linked anchor and
    // is already past the threshold.
    evaluate();

    window.addEventListener("scroll", evaluate, { passive: true });
    return () => window.removeEventListener("scroll", evaluate);
  }, [threshold]);

  return scrolled;
}
